import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, Loader2, Hash } from "lucide-react";
import { fetchProducts } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { Button } from "../components/ui/button";

export default function ModelLookup() {
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const { data, isFetching, isError } = useQuery({
    queryKey: ["model-lookup", query],
    queryFn: () => fetchProducts({ q: query, limit: 48 }),
    enabled: query.length >= 2,
  });

  const items = data?.items || [];
  const total = data?.total ?? 0;

  const submit = (e) => {
    e.preventDefault();
    const q = input.trim();
    if (q.length < 2) return;
    setQuery(q);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <div className="text-center mb-10">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-rp-green/10 mb-4">
          <Hash className="h-6 w-6 text-rp-green" />
        </div>
        <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900">
          Appliance Model Lookup
        </h1>
        <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
          Enter your appliance model number or part number to find matching
          replacement parts from our catalog of 11,000+ SKUs.
        </p>
      </div>

      <form onSubmit={submit} className="flex gap-0 max-w-xl mx-auto shadow-sm">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="e.g. WF45R6100AW, DA97-17376B, W10882923"
          className="flex-1 h-14 px-4 rounded-l-lg border border-slate-300 text-base focus:outline-none focus:ring-2 focus:ring-rp-green/40"
        />
        <Button
          type="submit"
          className="h-14 px-6 rounded-l-none rounded-r-lg bg-rp-green hover:bg-rp-green-dark gap-2"
        >
          <Search className="h-5 w-5" />
          Search
        </Button>
      </form>

      <div className="mt-4 text-center text-sm text-muted-foreground">
        Tip: model stickers are usually inside the door, on the back panel, or behind the kick plate.
      </div>

      {isFetching && (
        <div className="flex justify-center py-16 text-muted-foreground gap-2">
          <Loader2 className="h-6 w-6 animate-spin text-rp-green" />
          Searching catalog…
        </div>
      )}

      {isError && (
        <div className="text-center py-12 text-red-600">
          Search failed. Make sure the API is running.
        </div>
      )}

      {!isFetching && query && (
        <div className="mt-10">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-slate-900">
              Results for “{query}”
            </h2>
            <p className="text-sm text-muted-foreground">{total.toLocaleString()} matches</p>
          </div>

          {items.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-slate-300 rounded-xl">
              <p className="text-muted-foreground">No parts found for this model.</p>
              <Link to="/contact" className="text-rp-green font-semibold mt-3 inline-block">
                Contact us for help →
              </Link>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {items.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
              {total > items.length && (
                <div className="mt-8 text-center">
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/boutique?q=${encodeURIComponent(query)}`)}
                  >
                    View all {total.toLocaleString()} results in shop →
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
